import { Box, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useApiDuplicatePolicy,
	type PolicyOut,
	type PolicySeverity,
} from '@repo/api-codegen';
import { FilterOptionType } from '@repo/common/components/Filter/types';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import SingleSelector from '@repo/common/components/SingleSelector/SingleSelector';
import { EntityType } from '@repo/common/enums/entityType';
import { Text, Title } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { queryClient } from '../../api';
import { policiesQueryKeyFactory } from '../../api/hooks/policy';
import { closeSpotlight } from '../../components/Spotlight';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { useDeleteManyAction } from '../../components/TableV2/BaseModel.hooks';
import { DateTimeRender } from '../../components/TableV2/render';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';
import { PolicyToggle } from '../PolicyPage/PolicyToggle';
import { FrameworkSelector } from './components/FrameworkSelector';
import {
	useDeletePolicy,
	useTogglePolicyEnable,
	useUpdatePolicy,
} from './hooks';
import { PolicyStatusRender } from './render';

export function useColumns(): ExtendedDataTableColumn<PolicyOut>[] {
	const { mutateAsync: togglePolicy } = useTogglePolicyEnable({});

	const toggleCallBack = useCallback(
		(record: PolicyOut, enabled: boolean) => {
			togglePolicy({
				body: {
					enabled,
				},
				pathParams: {
					policyId: record.id,
				},
			});
		},
		[togglePolicy]
	);

	const { mutateAsync: updatePolicy } = useUpdatePolicy({});

	const updateCallback = useCallback(
		(key: string) => (id: string) => (value: string[] | string | boolean) => {
			updatePolicy({
				body: {
					[key]: value,
				},
				pathParams: {
					policyId: id,
				},
			});
		},
		[updatePolicy]
	);

	const columns: ExtendedDataTableColumn<PolicyOut>[] = useMemo(
		() => [
			{
				accessor: 'name',
				titleLabel: 'Policies',
				render: (record) => (
					<Stack role="link" spacing={0} py="xs">
						<Title size="sm" truncate>
							{record.name}
						</Title>
						{/* TODO[tan-policies]: use rich editor for the description render */}
						<Text size="xs" color="text/secondary/default" truncate>
							{record.description}
						</Text>
					</Stack>
				),
				width: 'auto',
			},
			{
				accessor: 'severity',
				titleLabel: 'Severity',
				navigate: false,
				render: (record) => (
					<SingleSelector
						variant="tertiary"
						searchable
						readOnly={false}
						property="severity"
						placeholder={`No severity`}
						options={SELECTABLE_PROPERTY_OPTIONS['severity']}
						onChange={(severity: PolicySeverity) => {
							updateCallback('severity')(record.id)(severity);
						}}
						isViewerUser={false}
						initialSelected={record.severity ?? 'NONE'}
						iconType={'tabler'}
						hideOnEmpty
					/>
				),
				width: 100,
			},
			{
				accessor: 'status',
				titleLabel: 'Status',
				render: (record) => <PolicyStatusRender record={record} />,
				filterOptionType: FilterOptionType.POLICY_STATUS,
				width: 220,
			},
			{
				accessor: 'results_count',
				titleLabel: 'Issues',
				textAlignment: 'right',
				resizable: false,
				render: (record) => (
					<Text size="sm">{(record.results_count ?? 0).toLocaleString()}</Text>
				),
				width: 80,
			},
			{
				accessor: 'framework',
				titleLabel: 'Frameworks',
				navigate: false,
				render: (record) => <FrameworkSelector policy={record} />,
				width: 180,
			},
			{
				accessor: 'last_run_at',
				titleLabel: 'Last scan',
				navigate: false,
				render: (record) => (
					<DateTimeRender record={record} field="last_run_at" />
				),
				width: 180,
			},
			{
				accessor: 'enabled',
				titleLabel: 'State',
				navigate: false,
				render: (record) => (
					<Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
						<PolicyToggle
							enabled={record.enabled}
							onChange={(enabled) => toggleCallBack(record, enabled)}
						/>
					</Box>
				),
				width: 60,
			},
		],
		[toggleCallBack, updateCallback]
	);

	return columns;
}

export const useActions = (): ICommandListItem<PolicyOut>[] => {
	const { mutateAsync: deletePolicy } = useDeletePolicy({});
	const { mutateAsync: duplicatePolicy } = useApiDuplicatePolicy({});

	const onDelete = useCallback(
		(id: string) => deletePolicy({ pathParams: { policyId: id } }),
		[deletePolicy]
	);

	const { action: deleteManyAction } = useDeleteManyAction<PolicyOut>({
		name: 'policy',
		queryKeys: ['policy', 'policies'],
		onDelete,
	});

	const actions = useMemo(
		() => [
			{
				id: 'actions::duplicate',
				title: 'Duplicate',
				name: 'Duplicate',
				iconName: 'copy' as const,
				hotkey: '/dp',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: PolicyOut[]) => selected.length === 1,
				onClick: async (selected: PolicyOut[], clearSelected) => {
					if (selected.length === 1) {
						try {
							await duplicatePolicy({
								pathParams: { policyId: selected[0].id },
							});
							queryClient.invalidateQueries({
								queryKey: policiesQueryKeyFactory.all(),
							});
							showNotification({
								message: 'Successfully duplicated policy',
								color: 'green',
							});
						} catch {
							showNotification({
								message: 'Failed to duplicate policy',
								color: 'red',
							});
						} finally {
							clearSelected();
							closeSpotlight('bulkActions');
						}
					}
				},
			},
			deleteManyAction,
		],
		[deleteManyAction, duplicatePolicy]
	);

	return actions;
};
